import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Row, Col, Typography, theme, Form, Input, Button, message, Flex, Modal, Checkbox } from 'antd'
import moment from 'moment'
import ContentCard from '../../components/layouts/ContentCard'
import DateMaskInput from '../../components/elements/DateMaskInput'
import { useUser } from '../../hooks/useUser'
import { isEmpty } from 'lodash'
import { track } from '../../utils/analytics'
import BackButton from '../../components/elements/BackButton'
import { HelmetHeader } from '../../components/elements/HelmetHeader'

export default function RegisterProfile() {
  const [isPolicyAgreed, setIsPolicyAgreed] = useState(false)
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const { updateProfile, user, logOut } = useUser()
  const { token } = theme.useToken()
  const { setFieldsValue } = form

  useEffect(() => {
    if (!isEmpty(user)) {
      const { firstName = '', lastName = '', birthDate = '' } = user

      setFieldsValue({
        firstName,
        lastName,
        birthDate: birthDate ? moment(birthDate).format('DD/MM/YYYY') : '',
      })
    }
  }, [user, setFieldsValue])

  const onSubmit = async (values) => {
    if (!isPolicyAgreed) {
      return Modal.info({
        className: 'centered-content-modal',
        centered: true,
        title: 'יש לאשר את התנאים כדי להמשיך בהרשמה',
        icon: null,
      })
    }

    try {
      if (!isEmpty(values)) {
        setIsLoading(true)
        const { firstName, lastName, birthDate } = values
        const payload = {}

        if (firstName) payload.firstName = firstName
        if (lastName) payload.lastName = lastName
        if (birthDate) {
          const date = moment.utc(birthDate, 'DD/MM/YYYY')

          if (date.isValid()) {
            payload.birthDate = date.format()
          }
        }

        await updateProfile(payload)

        track({
          event_name: 'form_submit',
          form_name: 'onboarding_profile',
          purpose: 'user_profile',
          flow: 'sign_up',
        })
      }

      navigate('/', {
        state: {
          isWelcomePopup: true,
        },
      })
    } catch (error) {
      message.error(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const onInputFocus = () => {
    if (!isInputFocused) {
      setIsInputFocused(true)
      track({
        event_name: 'form_start',
        form_name: 'onboarding_profile',
        purpose: 'user_profile',
        flow: 'sign_up',
      })
    }
  }

  return (
    <Form form={form} onFinish={onSubmit} validateTrigger="onSubmit">
      <HelmetHeader />
      <Row className="bg bg2" justify="center" style={{ minHeight: '100vh' }}>
        <Col align="middle" flex="724px">
          <ContentCard
            title={
              <Flex>
                <BackButton onClick={logOut} />
              </Flex>
            }
          >
            <Row gutter={[24, 24]} justify="center">
              <Col flex="550px">
                <Flex vertical gap={24} align="center">
                  <Flex vertical justify="center" align="center">
                    <Typography.Title style={{ color: token.colorPrimary, maxWidth: 340 }} level={3}>
                      {'ברוכים הבאים לאזור האישי של ערוץ הספורט!'}
                    </Typography.Title>
                    <Typography.Text style={{ fontSize: 24, maxWidth: 500 }}>
                      {'בואו נתחיל בהיכרות קצרה- אנחנו ערוץ הספורט, איך קוראים לך?'}
                    </Typography.Text>
                  </Flex>

                  <Row gutter={[0, 24]}>
                    <Col xs={24}>
                      <Row gutter={[8, 8]}>
                        <Col xs={24} sm={12}>
                          <Form.Item name="firstName" rules={[{ required: true, message: 'יש להזין שם פרטי' }]}>
                            <Input placeholder="שם פרטי" onFocus={onInputFocus} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item name="lastName" rules={[{ required: true, message: 'יש להזין שם משפחה' }]}>
                            <Input placeholder="שם משפחה" onFocus={onInputFocus} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={24}>
                      <Flex vertical gap={24} align="center">
                        <div style={{ maxWidth: 360 }}>
                          <Typography.Text style={{ fontSize: 24 }}>
                            לא בא לנו לשכוח להגיד לך מזל טוב. מתי נולדת?
                          </Typography.Text>
                        </div>
                        <Form.Item
                          name="birthDate"
                          rules={[
                            {
                              required: true,
                              message: 'יש להזין תאריך לידה',
                            },
                            {
                              validator: (_, value) => {
                                const date = moment(value, 'DD/MM/YYYY')
                                const now = moment()

                                if (!date.isValid() || date?.isAfter(now)) return Promise.reject('התאריך אינו תקין')
                                return Promise.resolve()
                              },
                            },
                          ]}
                        >
                          <DateMaskInput onFocus={onInputFocus} />
                        </Form.Item>
                      </Flex>
                    </Col>

                    <Col xs={24}>
                      <Flex gap={12} align="flex-start">
                        <Checkbox
                          defaultChecked={isPolicyAgreed}
                          onChange={(e) => setIsPolicyAgreed(e.target.checked)}
                        />
                        <Typography.Text style={{ fontSize: 12, textAlign: 'right' }}>
                          {
                            ' אני מאשר קבלת חומרים שיווקיים, עדכונים ומידע, של ערוץ הספורט ושל שותפיו העסקיים, באמצעות שיחות, מסרונים, דוא"ל או כל תקשורת דיגיטלית אחרת. תוכל בכל עת לבטל הסכמתך באמצעות פנייה בדוא"ל ל '
                          }
                          <a style={{ textDecoration: 'underline', color: 'inherit' }} href="mailto:support@rge.co.il">
                            {'support@rge.co.il'}
                          </a>
                          {` או באמצעות דרכי ההסרה שיפורטו בהודעה. `}
                        </Typography.Text>
                      </Flex>
                    </Col>
                  </Row>

                  <Button
                    block
                    shape="round"
                    type="primary"
                    htmlType="submit"
                    style={{ maxWidth: 171 }}
                    loading={isLoading}
                  >
                    {'המשך'}
                  </Button>
                </Flex>
              </Col>
            </Row>
          </ContentCard>
        </Col>
      </Row>
    </Form>
  )
}
